<template>
  <div>
    <!-- Header com busca e botão novo -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex-1 mr-4 flex items-center gap-3">
        <input 
          v-model="searchTerm" 
          @input="buscarClientes"
          type="text" 
          :placeholder="t('financeiro.clientes.searchPlaceholder')" 
          class="w-full px-4 py-2 rounded-lg border"
          style="background-color: var(--bg-primary); color: var(--text-primary); border-color: var(--border)"
        />
        <div class="view-toggle">
          <button 
            :class="['view-toggle-btn', viewMode === 'grid' ? 'view-toggle-active' : '']"
            @click="viewMode = 'grid'"
          >
            🟦 {{ t('financeiro.clientes.grid') }}
          </button>
          <button 
            :class="['view-toggle-btn', viewMode === 'list' ? 'view-toggle-active' : '']"
            @click="viewMode = 'list'"
          >
            ☰ {{ t('financeiro.clientes.list') }}
          </button>
        </div>
      </div>
      <button @click="abrirModalNovoCliente" class="btn-success">
        + {{ t('financeiro.clientes.newClient') }}
      </button>
    </div>

    <!-- Lista de Clientes -->
    <div v-if="viewMode === 'grid'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div v-for="cliente in clientes" :key="cliente.id" class="card hover:shadow-lg transition-all">
        <div class="flex items-start justify-between mb-3">
          <div class="flex items-center cursor-pointer flex-1" @click="abrirModalEditarCliente(cliente)">
            <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg mr-3">
              {{ cliente.nome.charAt(0).toUpperCase() }}
            </div>
            <div>
              <h3 class="font-semibold" style="color: var(--text-primary)">{{ cliente.nome }}</h3>
              <p class="text-sm" style="color: var(--text-secondary)">{{ cliente.cpfCnpj || t('financeiro.clientes.noCpfCnpj') }}</p>
            </div>
          </div>
          <span v-if="cliente.ativo" class="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">{{ t('financeiro.clientes.active') }}</span>
        </div>
        
        <div class="space-y-1 text-sm mb-3 cursor-pointer" @click="abrirModalEditarCliente(cliente)">
          <p v-if="cliente.email" style="color: var(--text-secondary)">📧 {{ cliente.email }}</p>
          <p v-if="cliente.telefone" style="color: var(--text-secondary)">📞 {{ cliente.telefone }}</p>
          <p v-if="cliente.nomeEmpresa" style="color: var(--text-secondary)">🏢 {{ cliente.nomeEmpresa }}</p>
        </div>

        <div class="cliente-meta">
          <div class="cliente-servicos">
            <span 
              v-for="tipo in getTiposServico(cliente.id)" 
              :key="tipo"
              :class="['cliente-servico-chip', getServicoClass(tipo)]"
            >
              <span class="servico-icon">{{ getServicoIcon(tipo) }}</span>
              {{ getServicoLabel(tipo) }}
            </span>
          </div>
          <div class="cliente-indicadores">
            <button 
              v-if="hasCobrancas(cliente.id)"
              class="cliente-icon-btn"
              :title="t('financeiro.clientes.viewCharges')"
              @click.stop="abrirCobrancasCliente(cliente)"
            >
              💵 <span class="cliente-icon-count">{{ getCobrancas(cliente.id).length }}</span>
            </button>
            <button 
              v-if="getAtrasos(cliente.id).length"
              class="cliente-icon-btn cliente-icon-danger"
              :title="t('financeiro.clientes.viewOverdue')"
              @click.stop="abrirAtrasosCliente(cliente)"
            >
              ⚠️ <span class="cliente-icon-count">{{ getAtrasos(cliente.id).length }}</span>
            </button>
          </div>
        </div>

        <!-- Botão Nova Cobrança -->
        <button 
          @click.stop="abrirModalNovaCobrancaParaCliente(cliente)" 
          class="w-full mt-3 px-3 py-2 rounded-lg font-medium transition-all bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center gap-2"
        >
          💵 {{ t('financeiro.clientes.newCharge') }}
        </button>
      </div>
    </div>
    <div v-else class="cliente-list">
      <div v-for="cliente in clientes" :key="cliente.id" class="cliente-list-row">
        <div class="cliente-list-main" @click="abrirModalEditarCliente(cliente)">
          <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
            {{ cliente.nome.charAt(0).toUpperCase() }}
          </div>
          <div class="cliente-list-info">
            <div class="flex items-center gap-2">
              <h3 class="font-semibold" style="color: var(--text-primary)">{{ cliente.nome }}</h3>
              <span v-if="cliente.ativo" class="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">{{ t('financeiro.clientes.active') }}</span>
            </div>
            <div class="text-sm" style="color: var(--text-secondary)">
              {{ cliente.cpfCnpj || t('financeiro.clientes.noCpfCnpj') }}
              <span v-if="cliente.email"> • {{ cliente.email }}</span>
              <span v-if="cliente.telefone"> • {{ cliente.telefone }}</span>
            </div>
          </div>
        </div>
        <div class="cliente-list-servicos">
          <span 
            v-for="tipo in getTiposServico(cliente.id)" 
            :key="tipo"
            :class="['cliente-servico-chip', getServicoClass(tipo)]"
          >
            <span class="servico-icon">{{ getServicoIcon(tipo) }}</span>
            {{ getServicoLabel(tipo) }}
          </span>
        </div>
        <div class="cliente-list-actions">
          <button 
            v-if="hasCobrancas(cliente.id)"
            class="cliente-icon-btn"
            :title="t('financeiro.clientes.viewCharges')"
            @click.stop="abrirCobrancasCliente(cliente)"
          >
            💵 <span class="cliente-icon-count">{{ getCobrancas(cliente.id).length }}</span>
          </button>
          <button 
            v-if="getAtrasos(cliente.id).length"
            class="cliente-icon-btn cliente-icon-danger"
            :title="t('financeiro.clientes.viewOverdue')"
            @click.stop="abrirAtrasosCliente(cliente)"
          >
            ⚠️ <span class="cliente-icon-count">{{ getAtrasos(cliente.id).length }}</span>
          </button>
          <button 
            @click.stop="abrirModalNovaCobrancaParaCliente(cliente)" 
            class="px-3 py-2 rounded-lg font-medium transition-all bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
          >
            💵 {{ t('financeiro.clientes.newCharge') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Cliente -->
    <ModalCliente 
      v-if="modalClienteAberto" 
      :cliente="clienteSelecionado"
      @fechar="fecharModalCliente"
      @salvar="salvarCliente"
    />

    <!-- Modal Cobrança -->
    <ModalCobranca 
      v-if="modalCobrancaAberto" 
      :cobranca="cobrancaSelecionada"
      :clientePreSelecionado="clienteParaCobranca"
      @fechar="fecharModalCobranca"
      @salvar="salvarCobranca"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { intlLocaleFor, formatMoneyAmount } from '../../utils/format'
import moneyIcon from '../../assets/money-icon.png'
import api from '../../services/api'
import Swal from 'sweetalert2'

const { t, locale } = useI18n()
import ModalCliente from './ModalCliente.vue'
import ModalCobranca from './ModalCobranca.vue'

const emit = defineEmits(['atualizar'])

const clientes = ref([])
const searchTerm = ref('')
const viewMode = ref('grid')
const cobrancasPorCliente = ref({})
const cobrancasIndex = ref({})
const atrasosPorCliente = ref({})
const tiposServicoPorCliente = ref({})
const modalClienteAberto = ref(false)
const clienteSelecionado = ref(null)
const modalCobrancaAberto = ref(false)
const cobrancaSelecionada = ref(null)
const clienteParaCobranca = ref(null)

onMounted(() => {
  carregarClientes()
  carregarResumoClientes()
})

async function carregarClientes() {
  try {
    const response = await api.get('/financeiro/clientes')
    clientes.value = response.data
  } catch (error) {
    console.error('Erro ao carregar clientes:', error)
    Swal.fire('Erro', t('financeiro.clientes.loadError'), 'error')
  }
}

async function carregarResumoClientes() {
  try {
    const [cobrancasResponse, atrasosResponse] = await Promise.all([
      api.get('/financeiro/cobrancas'),
      api.get('/financeiro/pagamentos/atrasados')
    ])

    const cobrancas = cobrancasResponse.data || []
    const atrasos = atrasosResponse.data || []

    const porCliente = {}
    const tipos = {}
    const index = {}

    cobrancas.forEach((cobranca) => {
      if (!porCliente[cobranca.clienteId]) {
        porCliente[cobranca.clienteId] = []
      }
      porCliente[cobranca.clienteId].push(cobranca)
      index[cobranca.id] = cobranca

      if (!tipos[cobranca.clienteId]) {
        tipos[cobranca.clienteId] = new Set()
      }
      if (cobranca.tipoServico) {
        tipos[cobranca.clienteId].add(cobranca.tipoServico)
      }
    })

    const atrasosMap = {}
    atrasos.forEach((pagamento) => {
      if (!pagamento.clienteId) return
      if (!atrasosMap[pagamento.clienteId]) {
        atrasosMap[pagamento.clienteId] = []
      }
      atrasosMap[pagamento.clienteId].push(pagamento)
    })

    const tiposArray = {}
    Object.keys(tipos).forEach((clienteId) => {
      tiposArray[clienteId] = Array.from(tipos[clienteId])
    })

    cobrancasPorCliente.value = porCliente
    cobrancasIndex.value = index
    atrasosPorCliente.value = atrasosMap
    tiposServicoPorCliente.value = tiposArray
  } catch (error) {
    console.error('Erro ao carregar resumo dos clientes:', error)
  }
}

async function buscarClientes() {
  try {
    const response = await api.get('/financeiro/clientes', { params: { nome: searchTerm.value } })
    clientes.value = response.data
  } catch (error) {
    console.error('Erro ao buscar clientes:', error)
  }
}

function abrirModalNovoCliente() {
  clienteSelecionado.value = null
  modalClienteAberto.value = true
}

function abrirModalEditarCliente(cliente) {
  clienteSelecionado.value = cliente
  modalClienteAberto.value = true
}

function fecharModalCliente() {
  modalClienteAberto.value = false
  clienteSelecionado.value = null
}

async function salvarCliente(clienteData) {
  try {
    if (clienteSelecionado.value) {
      await api.put(`/financeiro/clientes/${clienteSelecionado.value.id}`, clienteData)
      Swal.fire(t('common.success'), t('financeiro.clientes.updated'), 'success')
    } else {
      await api.post('/financeiro/clientes', clienteData)
      Swal.fire(t('common.success'), t('financeiro.clientes.created'), 'success')
    }
    fecharModalCliente()
    carregarClientes()
    emit('atualizar')
  } catch (error) {
    console.error('Erro ao salvar cliente:', error)
    Swal.fire('Erro', error.response?.data?.message || t('financeiro.clientes.saveError'), 'error')
  }
}

function abrirModalNovaCobrancaParaCliente(cliente) {
  clienteParaCobranca.value = cliente
  cobrancaSelecionada.value = null
  modalCobrancaAberto.value = true
}

function abrirModalEditarCobranca(cobranca) {
  cobrancaSelecionada.value = cobranca
  clienteParaCobranca.value = null
  modalCobrancaAberto.value = true
}

function fecharModalCobranca() {
  modalCobrancaAberto.value = false
  cobrancaSelecionada.value = null
  clienteParaCobranca.value = null
}

async function salvarCobranca(cobrancaData) {
  try {
    await api.post('/financeiro/cobrancas', cobrancaData)
    Swal.fire(t('common.success'), t('financeiro.clientes.chargeCreated'), 'success')
    fecharModalCobranca()
    carregarResumoClientes()
    emit('atualizar')
  } catch (error) {
    console.error('Erro ao salvar cobrança:', error)
    const errorMsg = error.response?.data?.message || error.response?.data?.error || t('financeiro.clientes.chargeSaveError')
    Swal.fire('Erro', errorMsg, 'error')
  }
}

function getCobrancas(clienteId) {
  return cobrancasPorCliente.value[clienteId] || []
}

function hasCobrancas(clienteId) {
  return getCobrancas(clienteId).length > 0
}

function getAtrasos(clienteId) {
  return atrasosPorCliente.value[clienteId] || []
}

function getTiposServico(clienteId) {
  return tiposServicoPorCliente.value[clienteId] || []
}

function getServicoIcon(tipo) {
  if (tipo === 'MONKEY') return '🐵'
  if (tipo === 'ONEWAY') return '🚀'
  return '📦'
}

function getServicoLabel(tipo) {
  if (tipo === 'MONKEY') return t('financeiro.clientes.serviceMonkey')
  if (tipo === 'ONEWAY') return t('financeiro.clientes.serviceOneway')
  return t('financeiro.clientes.serviceOther')
}

function getServicoClass(tipo) {
  if (tipo === 'MONKEY') return 'servico-monkey'
  if (tipo === 'ONEWAY') return 'servico-oneway'
  return 'servico-outro'
}

function formatMoneyHtml(value) {
  const amount = formatMoneyAmount(value, locale.value)
  return `<span style="display:inline-flex;align-items:center;gap:4px;"><img src="${moneyIcon}" alt="" style="width:14px;height:14px;object-fit:contain" aria-hidden="true" />${amount}</span>`
}

function formatDate(date) {
  if (!date) return '-'
  return new Date(date).toLocaleDateString(intlLocaleFor(locale.value))
}

function abrirCobrancasCliente(cliente) {
  const cobrancas = getCobrancas(cliente.id)
  if (!cobrancas.length) {
    Swal.fire(t('financeiro.clientes.noCharges'), t('financeiro.clientes.noChargesText'), 'info')
    return
  }
  if (cobrancas.length === 1) {
    abrirModalEditarCobranca(cobrancas[0])
    return
  }

  const itens = cobrancas.map((c) => `
    <div class="cliente-cobranca-item" style="display:flex; align-items:center; justify-content:space-between; gap:12px; padding:12px 14px; border-radius:14px; border:1px solid #e5e7eb; background:linear-gradient(180deg,#ffffff 0%,#f8fafc 100%); box-shadow:0 6px 18px rgba(15,23,42,0.08);">
      <div style="display:flex; align-items:center; gap:10px; text-align:left;">
        <div style="width:42px; height:42px; border-radius:12px; background:#e0e7ff; color:#3730a3; display:flex; align-items:center; justify-content:center; font-size:18px;">💵</div>
        <div>
          <div style="font-weight:700; color:#0f172a; font-size:14px;">${c.descricao || t('financeiro.clientes.chargeDefault')}</div>
          <div style="font-size:12px; color:#64748b; margin-top:2px;">${c.tipoServico || '-'} • ${c.status || '-'}</div>
        </div>
      </div>
      <button data-id="${c.id}" class="cliente-cobranca-open" style="padding:8px 12px; border-radius:10px; background:#2563eb; color:#fff; font-weight:700; font-size:12px; box-shadow:0 6px 14px rgba(37,99,235,0.25);">' + t('common.open') + '</button>
    </div>
  `).join('')

  Swal.fire({
    title: t('financeiro.clientes.chargesOf', { name: cliente.nome }),
    html: `
      <div style="display:flex; flex-direction:column; gap:10px;">
        ${itens}
      </div>
      <div style="font-size:12px; color:#64748b; text-align:center; margin-top:10px;">${t('financeiro.clientes.openChargeHint')}</div>
    `,
    showConfirmButton: false,
    showCloseButton: true,
    didOpen: () => {
      document.querySelectorAll('.cliente-cobranca-open').forEach((btn) => {
        btn.addEventListener('click', () => {
          const cobrancaId = btn.getAttribute('data-id')
          const cobranca = cobrancasIndex.value[cobrancaId]
          if (cobranca) {
            Swal.close()
            abrirModalEditarCobranca(cobranca)
          }
        })
      })
    }
  })
}

function abrirAtrasosCliente(cliente) {
  const atrasos = getAtrasos(cliente.id)
  if (!atrasos.length) {
    Swal.fire(t('financeiro.clientes.noOverdue'), t('financeiro.clientes.noOverdueText'), 'info')
    return
  }

  const itens = atrasos.map((p) => `
    <div style="display:flex; align-items:center; justify-content:space-between; gap:10px; padding:10px 12px; border-radius:10px; border:1px solid #fee2e2; background:#fef2f2;">
      <div style="text-align:left;">
        <div style="font-weight:600; color:#7f1d1d;">${p.descricao || t('financeiro.clientes.overdueItem')}</div>
        <div style="font-size:12px; color:#b91c1c;">${t('financeiro.clientes.dueDate')}: ${formatDate(p.dataVencimento)} • ${formatMoneyHtml(p.valor || 0)}</div>
      </div>
      <button data-id="${p.cobrancaId}" class="cliente-cobranca-open" style="padding:6px 10px; border-radius:8px; background:#dc2626; color:#fff; font-weight:600; font-size:12px;">' + t('financeiro.clientes.chargeBtn') + '</button>
    </div>
  `).join('')

  Swal.fire({
    title: t('financeiro.clientes.overdueOf', { name: cliente.nome }),
    html: `<div style="display:flex; flex-direction:column; gap:8px;">${itens}</div>`,
    showConfirmButton: false,
    showCloseButton: true,
    didOpen: () => {
      document.querySelectorAll('.cliente-cobranca-open').forEach((btn) => {
        btn.addEventListener('click', () => {
          const cobrancaId = btn.getAttribute('data-id')
          const cobranca = cobrancasIndex.value[cobrancaId]
          if (cobranca) {
            Swal.close()
            abrirModalEditarCobranca(cobranca)
          }
        })
      })
    }
  })
}
</script>

<style scoped>
.btn-success {
  @apply px-4 py-2 rounded-lg font-medium transition-all bg-green-600 text-white hover:bg-green-700;
}

.view-toggle {
  display: inline-flex;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 0.2rem;
  gap: 0.2rem;
}

.view-toggle-btn {
  padding: 0.45rem 0.9rem;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary);
  transition: all 0.2s ease;
}

.view-toggle-btn:hover {
  background-color: var(--bg-primary);
  color: var(--text-primary);
}

.view-toggle-active {
  background-color: var(--bg-primary);
  color: var(--text-primary);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.card {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
}

.cliente-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.cliente-servicos {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.cliente-servico-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  border: 1px solid var(--border);
  background-color: var(--bg-primary);
  color: var(--text-secondary);
}

.cliente-servico-chip .servico-icon {
  font-size: 0.85rem;
}

.servico-monkey {
  background: rgba(16, 185, 129, 0.15);
  color: #047857;
  border-color: rgba(16, 185, 129, 0.35);
}

.servico-oneway {
  background: rgba(59, 130, 246, 0.15);
  color: #1d4ed8;
  border-color: rgba(59, 130, 246, 0.35);
}

.servico-outro {
  background: rgba(249, 115, 22, 0.15);
  color: #c2410c;
  border-color: rgba(249, 115, 22, 0.35);
}

.cliente-indicadores {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.cliente-icon-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background-color: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.75rem;
  font-weight: 700;
  transition: all 0.2s ease;
}

.cliente-icon-btn:hover {
  border-color: rgba(59, 130, 246, 0.5);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.15);
}

.cliente-icon-danger {
  background-color: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.35);
  color: #dc2626;
}

.cliente-icon-count {
  padding: 0 0.25rem;
  border-radius: 999px;
  background-color: rgba(15, 23, 42, 0.08);
}

.cliente-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cliente-list-row {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: center;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1rem 1.25rem;
}

.cliente-list-main {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  cursor: pointer;
}

.cliente-list-info {
  min-width: 0;
}

.cliente-list-servicos {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.cliente-list-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  justify-content: flex-end;
}

@media (max-width: 1024px) {
  .cliente-list-row {
    grid-template-columns: 1fr;
  }
  .cliente-list-actions {
    justify-content: flex-start;
  }
}
</style>

